import { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Rating,
} from "@mui/material";

import API from "../api/api";

export default function GlobalDeliveredRating() {
  const [reviewDialog, setReviewDialog] = useState(false);
  const [orderToReview, setOrderToReview] = useState(null);

  const [reviewStep, setReviewStep] = useState(1);

  const [ratingValue, setRatingValue] = useState(0);
  const [reviewText, setReviewText] = useState("");

  const [submittingReview, setSubmittingReview] = useState(false);

  const closeDialog = () => {
    setReviewDialog(false);
    setOrderToReview(null);
    setRatingValue(0);
    setReviewText("");
    setReviewStep(1);
  };

  // 🔥 FIX 1: Wrap the function in useCallback and declare its dependency
  const checkDeliveredOrders = useCallback(async () => {

    if (reviewDialog) return;

    const user = JSON.parse(localStorage.getItem("user"));

    if (!user) return;

    try {
      const res = await API.get(`/user/${user.id}/orders`);

      // 🛡️ SAFE CHECK: Ensure data is an array before filtering
      if (Array.isArray(res.data)) {
        const deliveredOrder = res.data
          .filter(
            (o) =>
              o.status === "DELIVERED" &&
              !o.is_reviewed &&
              !o.popup_review_shown
          )
          .sort(
            (a, b) =>
              new Date(b.created_at) -
              new Date(a.created_at)
          )[0];

        if (deliveredOrder) {
          await API.post(
            `/order/${deliveredOrder.order_id}/popup-shown`
          );

          setOrderToReview(deliveredOrder);
          setReviewDialog(true);
        }
      }
    } catch (err) {
      console.error("Rating popup error:", err);
    }
  }, [reviewDialog]); // <-- Dependency required by the hook

  // 🔥 FIX 2: Add the memoized function to the useEffect dependency array
  // Inside useEffect
  useEffect(() => {
    // Only check once when the component mounts / user changes
    checkDeliveredOrders();

    // Removed the setInterval completely to save massive API load
  }, [checkDeliveredOrders]);
  const submitReview = async (skipComment = false) => {
    if (!orderToReview || ratingValue === 0) return;

    setSubmittingReview(true);

    try {
      await API.post("/order/review", {
        order_id: orderToReview.order_id,
        rating: ratingValue,
        comment: skipComment ? "" : reviewText,
      });

      // 🔥 FIX 1: Show the Success Animation Step
      setReviewStep(3);

      // 🔥 FIX 2: Wait 2 seconds (2000ms), then close everything
      setTimeout(() => {
        closeDialog();
      }, 2000);

    } catch (err) {
      console.error(err);
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <Dialog
      open={reviewDialog}
      onClose={closeDialog}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: "24px",
          bgcolor: "#FFFFFF",
          border: "1px solid #ECE5DA",
          boxShadow: "0 8px 32px rgba(58, 44, 35, 0.1)",
          p: 1
        }
      }}
    >
      {/* ================= STEP 1: STARS ================= */}
      {reviewStep === 1 && (
        <>
          <DialogTitle sx={{ textAlign: "center", fontWeight: 900, color: "#3A2C23", pb: 1 }}>
            Rate Your Order
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Typography align="center" sx={{ color: "#6E655E", fontWeight: 500, mb: 2 }}>
              How was your food experience?
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", py: 2.5, bgcolor: "#FFFDF8", borderRadius: "20px", border: "1px solid #ECE5DA" }}>
              <Rating
                size="large"
                value={ratingValue}
                onChange={(e, value) => setRatingValue(value)}
                sx={{ color: "#F29D17", "& .MuiRating-iconEmpty": { color: "#ECE5DA" } }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button
              fullWidth variant="contained" disableElevation disabled={ratingValue === 0}
              onClick={() => setReviewStep(2)}
              sx={{
                bgcolor: "#F8C332", color: "#3A2C23", borderRadius: "16px", py: 1.5, fontWeight: 800, textTransform: "none", fontSize: "1rem",
                "&:hover": { bgcolor: "#F5B71D" },
                "&.Mui-disabled": { bgcolor: "#F2EEE8", color: "#A59D96" }
              }}
            >
              Next
            </Button>
          </DialogActions>
        </>
      )}

      {/* ================= STEP 2: COMMENT ================= */}
      {reviewStep === 2 && (
        <>
          <DialogTitle sx={{ textAlign: "center", fontWeight: 900, color: "#3A2C23", pb: 1 }}>
            Write a Review
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth multiline rows={4}
              placeholder="Tell us what you loved (or what we can improve)..."
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              sx={{
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#FFFDF8", borderRadius: "16px",
                  "& fieldset": { borderColor: "#ECE5DA" },
                  "&:hover fieldset": { borderColor: "#F29D17" },
                  "&.Mui-focused fieldset": { borderColor: "#F29D17", borderWidth: "2px" },
                  "& textarea": { color: "#2F2A25", fontWeight: 500 }
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1, gap: 1.5, flexWrap: "nowrap" }}>
            <Button
              fullWidth variant="outlined" disabled={submittingReview}
              onClick={() => submitReview(true)} // 🔥 FIX: Saves the stars without a comment!
              sx={{
                borderColor: "#ECE5DA", color: "#6E655E", borderRadius: "16px", py: 1.2, fontWeight: 800, textTransform: "none",
                "&:hover": { borderColor: "#d0c7bc", bgcolor: "#fcfaf5" }
              }}
            >
              Skip
            </Button>
            <Button
              fullWidth variant="contained" disableElevation disabled={submittingReview}
              onClick={() => submitReview(false)}
              sx={{
                bgcolor: "#F8C332", color: "#3A2C23", borderRadius: "16px", py: 1.2, fontWeight: 800, textTransform: "none",
                "&:hover": { bgcolor: "#F5B71D" },
                "&.Mui-disabled": { bgcolor: "#F2EEE8", color: "#A59D96" }
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </>
      )}

      {/* ================= STEP 3: SUCCESS ANIMATION ================= */}
      {reviewStep === 3 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, pb: 5, textAlign: 'center' }}>
          {/* Using a native emoji to keep it fast, but you can swap this for an <img /> if you have the exact asset! */}
          <Typography sx={{ fontSize: 80, mb: 1, lineHeight: 1 }}>🥰</Typography>
          <Typography variant="h6" fontWeight="800" color="#3A2C23">
            Rating Submitted!
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Thanks for your feedback.
          </Typography>
        </Box>
      )}
    </Dialog>
  );
}
